import { RSAA } from 'redux-api-middleware';
import * as Sentry from '@sentry/react';
import { createSelector } from 'reselect';
import backoff from '../helpers/backoff';
import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { TGetRootState, TAsyncDispatchResult } from 'types/shared';
import getRuntimeEnv from "helpers/env";

/**
 * # Different action types
 *
 * ## Updating the store
 *
 * 1. Updating a component setting
 * 2. Re-setting a components' settings
 * 3. Resetting all component settings
 *
 * ## GET requests
 *
 * 1. request
 * 2. success
 * 3. failure
 *
 * ## PUT requests
 *
 * 1. request
 * 2. success
 * 3. failure
 */
export const UPDATE_SETTINGS = 'OPUS/PERSISTENT_SETTINGS/UPDATE_SETTINGS';
export const RESET_SINGLE = 'OPUS/PERSISTENT_SETTINGS/RESET_SINGLE';
export const RESET_ALL = 'OPUS/PERSISTENT_SETTINGS/RESET_ALL';

/** Generic API Action Types */
export const REMOTE_REQUEST = 'OPUS/PERSISTENT_SETTINGS/REMOTE_REQUEST';
export const REMOTE_RETRY = 'OPUS/PERSISTENT_SETTINGS/REMOTE_RETRY';
export const REMOTE_FAILURE = 'OPUS/PERSISTENT_SETTINGS/REMOTE_FAILURE';

export const GET_SETTINGS_SUCCESS =
  'OPUS/PERSISTENT_SETTINGS/GET_SETTINGS_SUCCESS';
export const PUT_SETTINGS_SUCCESS =
  'OPUS/PERSISTENT_SETTINGS/PUT_SETTINGS_SUCCESS';

type TPersistentSettingsAction =
  | {
      type: typeof UPDATE_SETTINGS;
      payload: IPersistentSettingsState;
    }
  | {
      type: typeof RESET_SINGLE;
      payload: {
        componentId: string;
        settings: TComponentSettings;
      };
    }
  | {
      type:
        | typeof RESET_ALL
        | typeof PUT_SETTINGS_SUCCESS
        | typeof REMOTE_REQUEST
        | typeof REMOTE_RETRY
        | typeof REMOTE_FAILURE;
    }
  | {
      type: typeof GET_SETTINGS_SUCCESS;
      payload: ISettingsLookup;
    };

type TComponentSettings = {
  lastSaved: string;
} & any;

interface ISettingsLookup {
  [key: string]: TComponentSettings;
}

interface IPersistentSettingsState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  apiLastLoaded: string | undefined;
  apiLastSaved: string | undefined;
  settings: ISettingsLookup;
}

export const initialState: IPersistentSettingsState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  apiLastLoaded: undefined,
  apiLastSaved: undefined,
  settings: {},
};

export default function reducer(
  state = initialState,
  action: TPersistentSettingsAction
): IPersistentSettingsState {
  switch (action.type) {
    case UPDATE_SETTINGS: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case RESET_SINGLE: {
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.payload.componentId]: {
            ...action.payload.settings,
            lastSaved: iso(),
          },
        },
      };
    }
    case RESET_ALL: {
      return {
        ...state,
        settings: {},
      };
    }

    case PUT_SETTINGS_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        apiLastSaved: iso(),
      };
    }

    case GET_SETTINGS_SUCCESS: {
      const nextSettings: ISettingsLookup = {};

      if (action.payload && action.payload.settings) {
        Object.entries<ISettingsLookup>(action.payload.settings).forEach(
          ([componentId, componentSettings]) => {
            let needsUpdate = false;

            const stateLastSaved = state.settings?.[componentId]?.lastSaved;
            if (stateLastSaved) {
              const localLastSaved = new Date(stateLastSaved);
              const remoteLastSaved = new Date(componentSettings.lastSaved);

              const remoteIsMoreRecent = remoteLastSaved >= localLastSaved;

              if (remoteIsMoreRecent) {
                needsUpdate = true;
              }
            } else {
              needsUpdate = true;
            }

            if (needsUpdate) {
              nextSettings[componentId] = componentSettings;
            }
          }
        );
      }

      return {
        ...state,
        settings: { ...state.settings, ...nextSettings },
        pending: false,
        fulfilled: true,
        rejected: false,
        apiLastLoaded: iso(),
      };
    }

    case REMOTE_REQUEST: {
      return {
        ...state,
        pending: true,
      };
    }

    case REMOTE_RETRY: {
      return {
        ...state,
        pending: true,
      };
    }

    case REMOTE_FAILURE: {
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
      };
    }

    default:
      return state;
  }
}

export function saveSettingsToLocalStore(
  payload: IPersistentSettingsState
): TPersistentSettingsAction {
  return {
    type: UPDATE_SETTINGS,
    payload,
  };
}

export function resetLocalSettings(): TPersistentSettingsAction {
  return { type: RESET_ALL };
}

/**
 * This action creator initializes an empty object at a specific component_settings endpoint for a user.
 * Only called once per user, when we get a 404 while trying to load the settings.
 */
function initComponentSettingsForUser(): (dispatch: Dispatch) => Promise<any> {
  return async (dispatch: Dispatch): Promise<any> =>
    await dispatch(saveSettingsToRemoteStore({}) as any);
}

/**
 * This action creator takes ONE component setting, but saves the complete store to an external API.
 * We use optimistic updates to update the store on the client in the REQUEST.
 *
 * @export
 * @param componentId A unique component ID to uniqiely link the settings with your component instance
 * @param key         A key for your setting you want to save
 * @param value       The value for the setting you want to save. Can be anything, but an array, string or object is logical to use here
 * @returns
 */
export function updateSettings(
  componentId: string,
  settings: any
): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<void> {
    const { persistentSettings: currentState } = getState();

    const nextState = {
      ...currentState,
      settings: {
        ...currentState.settings,
        [componentId]: {
          ...currentState.settings[componentId],
          ...settings,
          lastSaved: iso(),
        },
      },
    };

    // dispatch the local store update
    await dispatch(saveSettingsToLocalStore(nextState) as any);

    // dispatch the remote store update
    await dispatch(saveSettingsToRemoteStore(nextState) as any);
  };
}

/**
 * This action creator takes ONE component setting and saves it in the KV store.
 * We use optimistic updates to update the store on the client in the REQUEST.
 *
 * @export
 * @param componentId A unique component ID to uniqiely link the settings with your component instance
 * @param key         A key for your setting you want to save
 * @param value          The value for the setting you want to save. Can be anything, but an array, string or object is logical to use here
 * @returns
 */
export function clearAllSettings(): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch): Promise<void> => {
    // dispatch the local store update
    await dispatch(resetLocalSettings());

    // dispatch the remote store update
    await dispatch(saveSettingsToRemoteStore({}) as any);
  };
}

export function resetSingleSetting(
  componentId: string,
  settings: any
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<void> => {
    dispatch({
      type: RESET_SINGLE,
      payload: {
        componentId,
        settings,
      },
    });

    // Get the updated state
    const { persistentSettings } = getState();

    dispatch(saveSettingsToRemoteStore(persistentSettings) as any);
  };
}

export function loadSettingsFromRemoteStore(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const userId = getUserId(getState());
    const { persistentSettings } = getState();

    // bail out if persistenSettings are loading or fulfilled
    if (persistentSettings.pending || persistentSettings.fulfilled) {
      return;
    }

    const actionResponse = await backoff(
      () =>
        dispatch({
          [RSAA]: {
            types: [REMOTE_REQUEST, GET_SETTINGS_SUCCESS, REMOTE_RETRY],
            endpoint: `/kv/user_${userId}_component_settings`,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
          },
        }),
      'An error occurred trying to load your component settings. Retrying for the %s time...'
    );

    // This should only happen once for every user
    if (actionResponse.error) {
      if (actionResponse.payload.status === 404) {
        return await dispatch(initComponentSettingsForUser() as any);
      }
    }
  };
}

function saveSettingsToRemoteStore(payload: any): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    try {
      const userId = getUserId(getState());

      const response = await backoff(
        () =>
          dispatch({
            [RSAA]: {
              types: [REMOTE_REQUEST, PUT_SETTINGS_SUCCESS, REMOTE_RETRY],
              endpoint: `/kv/user_${userId}_component_settings`,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify(sanitize(payload)),
            },
          }),
        'An error occurred trying to save your component settings. Retrying for the %s time...'
      );

      if (response.error) {
        throw response.payload;
      }

      return response;
    } catch (error) {
      // The final retry error will end up here. Log it to console.
      // eslint-disable-next-line no-console
      console.warn('Your component settings failed to be saved remotely.');
      // eslint-disable-next-line no-console
      console.error(error);

      // Report it to Sentry
      if (getRuntimeEnv().NODE_ENV === 'production') {
        Sentry.withScope((scope: any) => {
          scope.setExtras({ action: REMOTE_REQUEST, state: getState() });
          Sentry.captureException(error);
        });
      }

      // Dispatch a final failed state
      dispatch({ type: REMOTE_FAILURE });
    }
  };
}

/**
 * HELPERS
 */

/**
 * This function takes a component settings object and removes all the keys we do not
 * want to store to the external API. This ensures we can more reliably look at the API state
 * in the store without worrying about values being returned from API cache
 * @param list Object containing all component settings for this componet ID
 */
function sanitize(list: any): any {
  const copy: any = { ...list };

  const keys = [
    'pending',
    'fulfilled',
    'rejected',
    'apiLastLoaded',
    'apiLastSaved',
    '_pending',
  ];

  // Sanitize the store object before saving it to an API
  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line prefer-const
  for (let key of keys) {
    delete copy[key];
  }

  return copy;
}

function getApiState(
  state: IPersistentSettingsState
): IPersistentSettingsState {
  return {
    pending: state.pending,
    fulfilled: state.fulfilled,
    rejected: state.rejected,
    apiLastLoaded: state.apiLastLoaded,
    apiLastSaved: state.apiLastSaved,
    settings: state.settings,
  };
}

export type apiStatusType =
  | 'unknown'
  | 'unloaded'
  | 'loading'
  | 'ready'
  | 'errored';

/**
 * This redux selector takes redux state as argument and returns the different status keys
 * combined into a more human understandable status. This way, we differentiate between
 * storing the technical status code in redux and derived/computed values which we might need in the component.
 */
export const computeApiState = createSelector(
  [getApiState],
  (apiState): apiStatusType => {
    const { pending, fulfilled, rejected } = apiState;

    let apiStatus = 'unknown';
    if (!pending && !fulfilled && !rejected) {
      apiStatus = 'unloaded';
    } else if (pending && !fulfilled && !rejected) {
      apiStatus = 'loading';
    } else if (!pending && fulfilled && !rejected) {
      apiStatus = 'ready';
    } else if (!pending && rejected) {
      apiStatus = 'errored';
    }

    return apiStatus as apiStatusType;
  }
);

function iso(): string {
  return new Date().toISOString();
}

function getUserId(state: RootState): string {
  return state.authentication.profile.id;
}
